import type { ChangeEventHandler, MouseEventHandler, ReactNode } from 'react';

import Img from 'components/Img';
import { Checkbox } from 'components/InputUi';
import { OptionalLink } from 'components/Link';
import OptionalContainer from 'components/OptionalContainer';
import Price from 'components/Price';
import { ProductCardTechnicalInfo } from 'components/ProductCard';
import type { PriceModel } from 'models/price';
import { cn } from 'utils/classNames';

export interface Props {
	checked?: boolean;
	energySymbolAlt?: string;
	energySymbolSrc?: string;
	href?: string;
	id: string;
	imageAlt?: string;
	imageSrc?: string;
	onChange?: ChangeEventHandler<HTMLInputElement>;
	onLinkClick?: MouseEventHandler<HTMLAnchorElement>;
	price?: PriceModel;
	productSheetLink?: string;
	productSheetLinkDescription?: string;
	text?: ReactNode;
	title: string;
	value: string;
}

export default function CheckboxListRow({
	checked,
	energySymbolAlt,
	energySymbolSrc,
	href,
	id,
	imageAlt,
	imageSrc,
	onChange,
	onLinkClick,
	price,
	productSheetLink,
	productSheetLinkDescription,
	text,
	title,
	value,
}: Props) {
	return (
		<li className="relative flex items-start gap-4 text-sm">
			{imageSrc && (
				<Img
					src={imageSrc}
					alt={imageAlt}
					width={48}
					height={48}
					className="shrink-0"
					jpgOptimized={false}
				/>
			)}
			<div className="flex grow flex-col">
				<p className="font-bold">
					<OptionalLink
						href={href}
						onLinkClick={onLinkClick}
						fallback={null}
						linkClassName={cn(
							'hover:underline',
							// Cover the image with a pseudo element instead of having a
							// duplicate link.
							imageSrc &&
								'before:absolute before:inset-y-0 before:left-0 before:z-1 before:w-16',
						)}
					>
						<OptionalContainer
							as="span"
							hasContainer={Boolean(imageSrc)}
							// Make the link hit area larger. Can't set this on the link
							// itself since relative will contain its pseudo element.
							className="relative z-1 -mt-1 -mb-3 pt-1 pb-3"
						>
							{title}
						</OptionalContainer>
					</OptionalLink>
				</p>
				{text}
				{price && <Price price={price} size="xs" className="mt-2" />}

				{href && (
					<ProductCardTechnicalInfo
						className="mt-2"
						energySymbolAlt={energySymbolAlt}
						energySymbolSrc={energySymbolSrc}
						href={href}
						productSheetLink={productSheetLink}
						productSheetLinkDescription={productSheetLinkDescription}
					/>
				)}
			</div>
			<Checkbox
				id={id}
				label={title}
				hiddenLabel
				// Stack input and label in the same grid cell.
				className="ml-4 grid w-12 self-stretch"
				inputClassName="col-start-1 row-start-1 justify-self-end"
				labelClassName={cn(
					'col-start-1 row-start-1 place-self-stretch p-0',
					// Pull the label slightly outside of the row for a larger hit area.
					'-m-2',
					// Add a background to the entire row for clearer hover feedback.
					'hover:after:absolute hover:after:-inset-2 hover:after:-z-1 hover:after:rounded-xs hover:after:bg-subtle',
				)}
				checked={checked}
				onChange={onChange}
				value={value}
			/>
		</li>
	);
}
CheckboxListRow.displayName = 'CheckboxListRow';
